import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, PlusIcon, MinusIcon } from 'lucide-react';
interface Testimonial {
  name: string;
  role: string;
  quote: string;
  img: string;
}
const TESTIMONIALS: Testimonial[] = [{
  name: 'Sofia R.',
  role: 'Upper East Side, Manhattan',
  quote: 'Ally’s team transformed my apartment into a hotel suite. The attention to detail is unlike anything I’ve experienced in the city.',
  img: 'https://cdn.magicpatterns.com/patterns/generated-images/3e5d12e8-21ae-4443-81a8-4d95b33b9891.jpg'
}, {
  name: 'Daniel M.',
  role: 'Airbnb Host, Long Island City',
  quote: 'Every turnover is flawless and on time. My guest reviews jumped the moment I switched to Ally. Truly a boutique standard.',
  img: 'https://cdn.magicpatterns.com/patterns/generated-images/298c61ab-6431-487d-99f0-de56408351f6.jpg'
}, {
  name: 'Priya K.',
  role: 'Long Island',
  quote: 'Professional, warm, and meticulous. They treat my home with such care — it feels like a personal service, not a chore.',
  img: 'https://cdn.magicpatterns.com/patterns/generated-images/3e5d12e8-21ae-4443-81a8-4d95b33b9891.jpg'
}];
interface FAQ {
  q: string;
  a: string;
}
const FAQS: FAQ[] = [{
  q: 'Do I need to provide cleaning supplies?',
  a: 'No. We bring everything needed for a premium clean, including professional-grade supplies and equipment.'
}, {
  q: 'Are you pet friendly?',
  a: 'Yes — we love pets and use thoughtful practices to keep your furry family members comfortable and safe.'
}, {
  q: 'What is your cancellation policy?',
  a: 'We kindly require 24 hours’ notice. Appointments cancelled with less notice are subject to a 50% fee.'
}, {
  q: 'Do you offer Deep Cleaning?',
  a: 'Yes. We recommend a Deep Cleaning for first visits so we can establish the immaculate boutique standard your home deserves.'
}, {
  q: 'How do I pay?',
  a: 'For your convenience, we accept Zelle, Cash, and all major credit and debit cards.'
}, {
  q: 'Do you do laundry?',
  a: 'Yes. Laundry service is available as a tailored add-on to your cleaning appointment.'
}, {
  q: 'Are you insured?',
  a: 'Yes. Our team is professionally insured, so you can enjoy complete peace of mind.'
}, {
  q: 'What areas do you serve?',
  a: 'We proudly serve Manhattan, Brooklyn, and Queens.'
}, {
  q: 'Can I trust you with my keys?',
  a: 'Absolutely. We use secure key-management practices and the utmost discretion in every home we enter.'
}, {
  q: 'Do you offer organic products?',
  a: 'Yes. Organic and eco-conscious products are available upon request for a clean that feels good in every sense.'
}];
function Stars() {
  return <div className="flex gap-0.5" aria-label="5 out of 5 stars" data-id="element-240" data-mp="2ezym0t9bkz8c">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="h-4 w-4 fill-magenta text-magenta" data-id="element-241" data-mp="2jy8j59elu9wl" data-mpi={i} />)}
    </div>;
}
export function SocialProof() {
  const [open, setOpen] = useState<number | null>(0);
  return <section id="faq" className="w-full bg-white" data-id="element-242" data-mp="39z7rzshfrv9">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24" data-id="element-243" data-mp="3uhbnsbhmpz9l">
        {/* Testimonials */}
        <div className="mx-auto max-w-2xl text-center" data-id="element-244" data-mp="3pj0d5yjifkqb">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-magenta" data-id="element-245" data-mp="3pkl8vrhk99pr">
            Loved by New Yorkers
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate" data-id="element-246" data-mp="m2bib9tahzgh">
            What our clients say
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3" data-id="element-247" data-mp="h98m0tx4obwl">
          {TESTIMONIALS.map((t, i) => <motion.figure key={t.name} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.08
        }} className="flex flex-col rounded-3xl bg-slate-50 p-8 ring-1 ring-slate-100" data-id="element-248" data-mp="cae2fprc6jbf" data-mpi={i}>
              <Stars data-id="element-249" data-mp="kkl0wpfx5u8x" />
              <blockquote className="mt-5 flex-1 text-slate-soft leading-relaxed" data-id="element-250" data-mp="klw16fa5ojva">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3" data-id="element-251" data-mp="1d90b8yo3vodx">
                <img src={t.img} alt={t.name} className="h-11 w-11 rounded-full object-cover" data-id="element-252" data-mp="1dabemxdn2u4i" />
                <div data-id="element-253" data-mp="13hcmj5ixolg5">
                  <p className="font-display text-sm font-bold text-slate" data-id="element-254" data-mp="13gksw08wcxsi">
                    {t.name}
                  </p>
                  <p className="text-xs text-slate-muted" data-id="element-255" data-mp="1vyoq7ibkau7n">{t.role}</p>
                </div>
              </figcaption>
            </motion.figure>)}
        </div>

        {/* FAQ */}
        <div className="mx-auto mt-24 max-w-3xl" data-id="element-256" data-mp="3bqai9wpahpaq">
          <div className="text-center" data-id="element-257" data-mp="3bphkvmc80c59">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-brand" data-id="element-258" data-mp="3bog554k9f1h7">
              FAQ
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate" data-id="element-259" data-mp="3lfkd3kbipm2j">
              Questions, answered
            </h2>
          </div>

          <div className="mt-10 divide-y divide-slate-100 rounded-3xl ring-1 ring-slate-100" data-id="element-260" data-mp="2sreqes0wln02">
            {FAQS.map((faq, i) => {
            const isOpen = open === i;
            return <div key={faq.q} data-id="element-261" data-mp="32pm4n33t5qyd" data-mpi={i}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left" data-id="element-262" data-mp="32ojdcu2kpmhx">
                    <span className="font-display text-base font-semibold text-slate" data-id="element-263" data-mp="3audgpwf5uq3y">
                      {faq.q}
                    </span>
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-magenta-soft text-magenta" data-id="element-264" data-mp="3fr3toavo55l5">
                      {isOpen ? <MinusIcon className="h-4 w-4" data-id="element-265" data-mp="3oslh6ebeo9jv" /> : <PlusIcon className="h-4 w-4" data-id="element-266" data-mp="2s3vwyhcb34uq" />}
                    </span>
                  </button>
                  <AnimatePresence initial={false} data-id="element-267" data-mp="2x2yu7fm1o6s5">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.28
                }} className="overflow-hidden" data-id="element-268" data-mp="312zh7on7lqat">
                        <p className="px-6 pb-5 text-slate-soft leading-relaxed" data-id="element-269" data-mp="1wwd9678x1kok">
                          {faq.a}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}